import { DataType } from '@shared/extension/enums/data-type.enum';
import { StoreName } from '../../enums';
import { LearnedRecommendation, Recommendation, StudentState } from '../../interfaces';
import { Student, StudentThirdPartyAppCredentials } from '../../models';
import { AbstractLocalStorageService } from '../../services/abstract/abstract-local-storage.service';
import { AbstractStore } from './abstract.store';

export abstract class AbstractStudentStore extends AbstractStore<StudentState> {
  // Main selectors
  readonly currentUrl$ = this.select('currentUrl');
  readonly lastLearningActivityTime$ = this.select('lastLearningActivityTime');
  readonly learnedRecommendations$ = this.select('learnedRecommendations');
  readonly learningReminderDisplayTimes$ = this.select('learningReminderDisplayTimes');
  readonly moneyReward$ = this.select('moneyReward');
  readonly recommendations$ = this.select('recommendations');
  readonly recommendationsLoadingFailed$ = this.select('recommendationsLoadingFailed');
  readonly student$ = this.select('student');
  readonly studentBalance$ = this.select('studentBalance');
  readonly thirdPartyAppCredentialsList$ = this.select('thirdPartyAppCredentialsList');
  readonly waitingForRewardRecommendation$ = this.select('waitingForRewardRecommendation');
  readonly watchRecommendationsIntervalMs$ = this.select('watchRecommendationsIntervalMs');
  readonly watchStudentBalanceIntervalMs$ = this.select('watchStudentBalanceIntervalMs');

  // Mapped selectors
  readonly isLearningAnyRecommendation$ = this.mapValue(
    ['currentUrl', 'recommendations'],
    (currentUrl: string, recommendations: Recommendation[]) => {
      return recommendations?.some(item => item.url === currentUrl) ?? false;
    }
  );
  readonly studentId$ = this.mapValue('student', (value: Student) => value?.student_id || null);

  constructor(protected override localStorageService: AbstractLocalStorageService) {
    super(localStorageService, StoreName.StudentStore, {
      currentUrl: [null, DataType.String],
      lastLearningActivityTime: [null, DataType.Number],
      learnedRecommendations: [null, DataType.Object],
      learningReminderDisplayTimes: [null, DataType.Object],
      moneyReward: [null, DataType.Number],
      recommendations: [null, DataType.Object],
      recommendationsLoadingFailed: [false, DataType.Boolean],
      student: [null, DataType.Object],
      studentBalance: [null, DataType.Number],
      thirdPartyAppCredentialsList: [null, DataType.Object],
      waitingForRewardRecommendation: [null, DataType.Object],
      watchRecommendationsIntervalMs: [null, DataType.Number],
      watchStudentBalanceIntervalMs: [null, DataType.Number],
    });
  }

  setCurrentUrl(data: string | null): void {
    this.setValue('currentUrl', data);
  }

  async setLastLearningActivityTime(data: number | null): Promise<void> {
    await this.setValue('lastLearningActivityTime', data);
  }

  async setLearnedRecommendations(data: LearnedRecommendation[] | null): Promise<void> {
    await this.setValue('learnedRecommendations', data);
  }

  async setLearningReminderDisplayTimes(data: number[] | null): Promise<void> {
    await this.setValue('learningReminderDisplayTimes', data);
  }

  async setMoneyReward(data: number | null): Promise<void> {
    await this.setValue('moneyReward', data);
  }

  async setRecommendations(data: Recommendation[] | null): Promise<void> {
    await this.setValue('recommendations', data);
  }

  async setRecommendationsLoadingFailed(data: boolean): Promise<void> {
    await this.setValue('recommendationsLoadingFailed', data);
  }

  async setStudent(data: Student | null): Promise<void> {
    await this.setValue('student', data);
  }

  async setStudentBalance(data: number | null): Promise<void> {
    await this.setValue('studentBalance', data);
  }

  async setThirdPartyAppCredentialsList(data: StudentThirdPartyAppCredentials[] | null): Promise<void> {
    await this.setValue('thirdPartyAppCredentialsList', data);
  }

  async setWaitingForRewardRecommendation(data: Recommendation | null): Promise<void> {
    await this.setValue('waitingForRewardRecommendation', data);
  }

  async setWatchRecommendationsIntervalMs(data: number | null): Promise<void> {
    await this.setValue('watchRecommendationsIntervalMs', data);
  }

  async setWatchStudentBalanceIntervalMs(data: number | null): Promise<void> {
    await this.setValue('watchStudentBalanceIntervalMs', data);
  }
}
